import type { Leg, RunwayEnd } from './types';
import type { ScoringConfig } from './config';
import { NM_M } from './geo';

export interface IdealPoint { along: number; cross: number; hAF: number }
export interface IdealPattern {
  points: IdealPoint[];
  legAt: Leg[];                 // leg per point; arc points are labelled turn_<following leg>
  legBoundaryIdx: number[];     // 4 indices: mid-turn between upwind→crosswind, →downwind, →base, →final
}

const BASE_ABEAM_M = 2400;
const UPWIND_EXTEND_M = 2700;
const TOUCHDOWN_M = 250;
const TURN: Record<string, Leg> = { crosswind: 'turn_crosswind', downwind: 'turn_downwind', base: 'turn_base', final: 'turn_final' };

/** Ideal rectangular pattern with rounded corners, in the runway frame. */
export function idealPattern(rw: RunwayEnd, cfg: ScoringConfig, direction: 'left' | 'right'): IdealPattern {
  const s = direction === 'left' ? -1 : 1;
  const D = cfg.downwindNm * NM_M, r = cfg.turnRadiusNm * NM_M, L = rw.lengthM;
  const corners: { along: number; cross: number; hAF: number; leg: Leg }[] = [
    { along: 0, cross: 0, hAF: 0, leg: 'upwind' },
    { along: L + UPWIND_EXTEND_M, cross: 0, hAF: cfg.tpaFt * 0.8, leg: 'crosswind' },
    { along: L + UPWIND_EXTEND_M, cross: s * D, hAF: cfg.tpaFt, leg: 'downwind' },
    { along: -BASE_ABEAM_M, cross: s * D, hAF: cfg.tpaFt, leg: 'base' },
    { along: -BASE_ABEAM_M, cross: 0, hAF: 450, leg: 'final' },
    { along: TOUCHDOWN_M, cross: 0, hAF: 0, leg: 'final' },
  ];
  const points: IdealPoint[] = [], legAt: Leg[] = [], legBoundaryIdx: number[] = [];
  const push = (p: IdealPoint, leg: Leg) => { points.push(p); legAt.push(leg); };
  const STEP = 50;
  for (let k = 0; k < corners.length - 1; k++) {
    const a = corners[k]!, b = corners[k + 1]!;
    const ux = Math.sign(b.along - a.along), uy = Math.sign(b.cross - a.cross);
    const len = Math.hypot(b.along - a.along, b.cross - a.cross);
    const trimA = k === 0 ? 0 : r, trimB = k === corners.length - 2 ? 0 : r;
    for (let d = trimA; d <= len - trimB; d += STEP) {
      const f = d / len;
      push({ along: a.along + ux * d, cross: a.cross + uy * d, hAF: a.hAF + (b.hAF - a.hAF) * f }, a.leg);
    }
    if (k === corners.length - 2) push({ along: b.along, cross: b.cross, hAF: b.hAF }, b.leg);   // exact touchdown point
    if (k < corners.length - 2) {
      const c = corners[k + 2]!;
      const vx = Math.sign(c.along - b.along), vy = Math.sign(c.cross - b.cross);
      const S = { x: b.along - ux * r, y: b.cross - uy * r }, C = { x: S.x + vx * r, y: S.y + vy * r };
      const n = 6;
      for (let i = 1; i <= n; i++) {
        const t = (i / (n + 1)) * (Math.PI / 2);
        const x = C.x - vx * r * Math.cos(t) + ux * r * Math.sin(t), y = C.y - vy * r * Math.cos(t) + uy * r * Math.sin(t);
        if (i === Math.ceil(n / 2)) legBoundaryIdx.push(points.length);
        push({ along: x, cross: y, hAF: b.hAF }, TURN[b.leg]!);
      }
    }
  }
  return { points, legAt, legBoundaryIdx };
}
